/**
 * PageTransition — Phase 12: Premium design system.
 *
 * Wraps every route's content in a subtle fade-up so route changes feel
 * intentional rather than abrupt. Uses framer-motion (already in the bundle
 * from TaskDetailDrawer). Respects `prefers-reduced-motion`.
 *
 * Usage:
 *   <PageTransition>
 *     <YourPage />
 *   </PageTransition>
 *
 * Applied in App.tsx around each <Route> element via the RouteTransition
 * helper exported below.
 */

import React from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { useLocation } from 'react-router-dom'

const TRANSITION = {
  duration: 0.22,
  ease: [0.16, 1, 0.3, 1], // --ease-out token
}

const variants = {
  initial:  { opacity: 0,   y: 6 },
  animate:  { opacity: 1,   y: 0 },
  exit:     { opacity: 0,   y: -4 },
}

const reducedVariants = {
  initial:  { opacity: 0 },
  animate:  { opacity: 1 },
  exit:     { opacity: 0 },
}

interface PageTransitionProps {
  children: React.ReactNode
  /** Override the animation key — defaults to the current pathname */
  animationKey?: string
}

export function PageTransition({ children, animationKey }: PageTransitionProps) {
  const location   = useLocation()
  const shouldReduce = useReducedMotion()
  const v = shouldReduce ? reducedVariants : variants

  return (
    <motion.div
      key={animationKey ?? location.pathname}
      initial="initial"
      animate="animate"
      exit="exit"
      variants={v}
      transition={TRANSITION}
      style={{ willChange: 'opacity, transform' }}
    >
      {children}
    </motion.div>
  )
}

/**
 * Convenience wrapper for a single route element.
 * Keeps App.tsx tidy — wrap once, not per-page.
 */
export function withPageTransition<P extends object>(
  Component: React.ComponentType<P>,
): React.FC<P> {
  return function TransitionedPage(props: P) {
    return (
      <PageTransition>
        <Component {...props} />
      </PageTransition>
    )
  }
}
